import { useState } from 'react';
import type { CreativeBriefInput, CreativeChannel } from '../../shared/types';
import { CREATIVE_CHANNEL_OPTIONS } from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';

interface Props {
  error: string | null;
  onSubmit: (data: { textContent: string }) => void;
  onBack: () => void;
}

export default function CreativeBriefForm({ error, onSubmit, onBack }: Props) {
  const [brand, setBrand] = useState('');
  const [product, setProduct] = useState('');
  const [positioning, setPositioning] = useState('');
  const [target, setTarget] = useState('');
  const [channel, setChannel] = useState<CreativeChannel>('social');
  const [message, setMessage] = useState('');
  const [constraints, setConstraints] = useState('');

  const canSubmit = brand.trim() && product.trim() && target.trim() && message.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const brief: CreativeBriefInput = {
      brand: brand.trim(),
      product: product.trim(),
      positioning: positioning.trim() || undefined,
      target: target.trim(),
      channel,
      message: message.trim(),
      constraints: constraints.trim() || undefined,
    };
    // Send as JSON string in textContent — backend parses it
    onSubmit({ textContent: JSON.stringify(brief) });
  };

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      {/* Back + Title — mode pastel : bandeau à l'aplat de la typologie,
          icône seule dans une pastille blanche (cf. ContentUploader). */}
      <div className="p-hero flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          aria-label="Retour"
          className="p-back p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors text-slate-500 shader:text-white/60"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div className="p-hero-pill hidden pastel:grid" aria-hidden="true">
          <AnalysisIcon type="creative_brief" className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-xl text-slate-900 dark:text-white pastel:text-(--p-ink) flex items-center gap-2">
            <AnalysisIcon type="creative_brief" className="w-5 h-5 text-slate-400 dark:text-white/50 pastel:hidden" />
            &Eacute;vin Cr&eacute;a
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 pastel:text-(--p-ink)/70">
            D&eacute;crivez votre brief cr&eacute;atif pour recevoir des id&eacute;es marketing conformes &agrave; la Loi &Eacute;vin
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 shader:bg-red-900/20 shader:backdrop-blur-sm border border-red-200 dark:border-red-800 shader:border-red-500/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Row 1: Brand + Product */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
              Marque *
            </label>
            <div className="glow-input">
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder='Ex: "Maison Duval"'
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-2.5 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
              Produit *
            </label>
            <div className="glow-input">
              <input
                type="text"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder='Ex: "Bi&egrave;re blonde artisanale"'
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-2.5 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
              />
            </div>
          </div>
        </div>

        {/* Positioning (optional) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Positionnement
            <span className="text-slate-400 font-normal ml-1">(optionnel)</span>
          </label>
          <div className="glow-input">
            <input
              type="text"
              value={positioning}
              onChange={(e) => setPositioning(e.target.value)}
              placeholder='Ex: "Premium, terroir proven&ccedil;al, &eacute;co-responsable"'
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-2.5 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
            />
          </div>
        </div>

        {/* Target audience */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Cible *
          </label>
          <div className="glow-input">
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder='Ex: "Hommes et femmes 30-50 ans, amateurs de gastronomie"'
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-2.5 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
            />
          </div>
        </div>

        {/* Channel */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Canal de diffusion
          </label>
          <div className="flex flex-wrap gap-2">
            {CREATIVE_CHANNEL_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setChannel(opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  channel === opt.value
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 shader:bg-white/5 text-slate-600 dark:text-slate-300 shader:text-white/60 hover:bg-slate-200 dark:hover:bg-slate-700 shader:hover:bg-white/10'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Message &agrave; faire passer *
          </label>
          <div className="glow-input">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder='Ex: "Mettre en avant le savoir-faire artisanal et l&#39;ancrage local de la brasserie pour la saison estivale"'
              rows={3}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y min-h-[3rem]"
            />
          </div>
        </div>

        {/* Constraints (optional) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1">
            Contraintes particuli&egrave;res
            <span className="text-slate-400 font-normal ml-1">(optionnel)</span>
          </label>
          <div className="glow-input">
            <textarea
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
              placeholder='Ex: "Budget limit&eacute;, pas de vid&eacute;o, doit inclure la mention sanitaire, partenariat avec un chef cuisinier"'
              rows={2}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y min-h-[2.5rem]"
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
            canSubmit
              ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30 hover:scale-[1.02] active:scale-[0.98]'
              : 'bg-slate-200 dark:bg-slate-700 shader:bg-white/10 text-slate-400 cursor-not-allowed'
          }`}
        >
          G&eacute;n&eacute;rer 6 id&eacute;es cr&eacute;atives
        </button>
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/10 shader:bg-violet-500/5 shader:backdrop-blur-sm border border-violet-200 dark:border-violet-800/30 shader:border-violet-500/10">
        <p className="text-sm text-violet-700 dark:text-violet-300 shader:text-violet-200/80">
          <strong>&Eacute;vin Cr&eacute;a</strong> g&eacute;n&egrave;re 6 territoires cr&eacute;atifs distincts, chacun valid&eacute; par notre moteur juridique.
          Chaque id&eacute;e inclut un concept, des slogans pr&ecirc;ts &agrave; l&apos;emploi, une direction artistique et les articles de loi qui l&apos;autorisent.
        </p>
      </div>
    </div>
  );
}
